/**
 * Модуль для работы с историей боев
 */

import { logger } from "../../utils/logger.js";

/**
 * Получает имя победителя боя
 */
function getBattleWinner(battle) {
  if (!battle || !battle.combatants) return null;

  const winner = battle.combatants.find((c) => c.winner === true);
  if (!winner) return null;

  return winner.ownerName || winner.owner || winner.name || null;
}

/**
 * Создаёт менеджер для работы с историей боев
 */
export function createBattleHistoryManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  escapeHtmlFn,
  showBattleModal,
  resetBattleView,
  loadBattleTimeline,
  renderBattleSummary,
  documentRef = document,
}) {
  function getAuthHeadersWithInitData() {
    try {
      const headers = buildAuthHeaders();
      if (!headers || typeof headers !== 'object') {
        logger.warn('[battleHistory] buildAuthHeaders returned invalid value:', headers);
        return null;
      }
      const initDataHeader =
        headers['X-Telegram-Init-Data'] || headers['x-telegram-init-data'];
      if (!initDataHeader || typeof initDataHeader !== 'string' || initDataHeader.trim() === '') {
        logger.warn('[battleHistory] No valid initData in headers:', Object.keys(headers));
        return null;
      }
      return headers;
    } catch (error) {
      logger.error('[battleHistory] Error getting auth headers:', error);
      return null;
    }
  }

  /**
   * Рендерит список истории боев
   */
  function renderBattleHistory(battles) {
    const domCache = window.__domCacheManager;
    const container = domCache?.getCachedElement("#battle-history", documentRef) 
      || documentRef.getElementById("battle-history");
    
    if (!container) return;

    if (!battles || battles.length === 0) {
      const emptyHTML = '<div class="battle-history-item">Пока нет боёв</div>';
      if (domCache) {
        domCache.updateHTML("#battle-history", emptyHTML, documentRef);
      } else {
        container.innerHTML = emptyHTML;
      }
      return;
    }

    const battlesHTML = battles
      .map((battle) => {
        const date = new Date(battle.createdAt).toLocaleString("ru-RU");
        const winner = getBattleWinner(battle) || "—";
        const arena = escapeHtmlFn(battle.arena || "Аренда");
        const status = escapeHtmlFn(battle.status || "");
        const isPublic = battle.isPublic ? " • 🌐" : "";

        return `
          <div class="battle-history-item">
            <div><strong>#${battle.id}</strong> • ${arena} • ${status}${isPublic}</div>
            <div style="font-size: 12px; color: var(--tg-theme-hint-color);">${date}</div>
            <div style="font-size: 12px;">Победитель: ${escapeHtmlFn(winner)}</div>
            <button class="btn btn-small" style="margin-top: 6px; width: 100%;" onclick="viewBattleDetail(${battle.id})">Открыть</button>
          </div>
        `;
      })
      .join("");
    
    if (domCache) {
      domCache.updateHTML("#battle-history", battlesHTML, documentRef);
    } else {
      container.innerHTML = battlesHTML;
    }
  }

  /**
   * Рендерит список публичных боев
   */
  function renderPublicBattles(battles) {
    const domCache = window.__domCacheManager;
    const container = domCache?.getCachedElement("#public-battle-feed", documentRef) 
      || documentRef.getElementById("public-battle-feed");
    
    if (!container) return;

    if (!battles || battles.length === 0) {
      const emptyHTML = '<div class="battle-history-item">Публичные бои ещё не проводились</div>';
      if (domCache) {
        domCache.updateHTML("#public-battle-feed", emptyHTML, documentRef);
      } else {
        container.innerHTML = emptyHTML;
      }
      return;
    }

    const battlesHTML = battles
      .map((battle) => {
        const date = new Date(battle.createdAt).toLocaleString("ru-RU");
        const winner = getBattleWinner(battle) || "—";
        const arena = escapeHtmlFn(battle.arena || "Аренда");
        const status = escapeHtmlFn(battle.status || "");

        return `
          <div class="battle-history-item">
            <div><strong>#${battle.id}</strong> • ${arena} • ${status} • 🌐</div>
            <div style="font-size: 12px; color: var(--tg-theme-hint-color);">${date}</div>
            <div style="font-size: 12px;">Победитель: ${escapeHtmlFn(winner)}</div>
            <button class="btn btn-small" style="margin-top: 6px; width: 100%;" onclick="viewBattleDetail(${battle.id})">Посмотреть</button>
          </div>
        `;
      })
      .join("");
  }

  /**
   * Загружает историю боев пользователя
   */
  // Защита от дублирующих вызовов
  let isLoadingHistory = false;
  let lastLoadTime = 0;
  const LOAD_THROTTLE_MS = 1000; // Минимальный интервал между загрузками (1 секунда)

  async function loadBattleHistory() {
    const container = documentRef.getElementById("battle-history");
    if (!container) return;

    // Защита от дублирующих вызовов
    const now = Date.now();
    if (isLoadingHistory || (now - lastLoadTime < LOAD_THROTTLE_MS)) {
      console.log('[battleHistory] Load already in progress or too soon, skipping');
      return;
    }

    isLoadingHistory = true;
    lastLoadTime = now;

    const headers = getAuthHeadersWithInitData();
    if (!headers) {
      container.innerHTML =
        '<div class="battle-history-item">История боёв доступна после завершения авторизации в Telegram Mini App.</div>';
      isLoadingHistory = false;
      return;
    }

    try {
      const apiUrl = getApiUrl();
      console.log('[battleHistory] Loading battle history from:', `${apiUrl}/battle/history?limit=10`);
      const response = await safeFetch(`${apiUrl}/battle/history?limit=10`, {
        headers,
      });

      if (!response.success) {
        console.error('[battleHistory] Failed to load history:', response.error, response.status);
        container.innerHTML =
          '<div class="battle-history-item">Не удалось загрузить историю</div>';
        return;
      }

      renderBattleHistory(response.data.battles || []);
    } catch (error) {
      console.error("loadBattleHistory error", error);
      container.innerHTML =
        '<div class="battle-history-item">Ошибка загрузки истории</div>';
    } finally {
      isLoadingHistory = false;
    }
  }

  /**
   * Загружает публичные бои
   */
  async function loadPublicBattles() {
    const container = documentRef.getElementById("public-battle-feed");
    if (!container) return;

    const headers = getAuthHeadersWithInitData();
    if (!headers) {
      container.innerHTML =
        '<div class="battle-history-item">Публичные бои появятся после завершения авторизации.</div>';
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/battle/public?limit=10`, {
        headers,
      });

      if (!response.success) {
        container.innerHTML =
          '<div class="battle-history-item">Не удалось загрузить публичные бои</div>';
        return;
      }

      renderPublicBattles(response.data.battles || []);
    } catch (error) {
      console.error("loadPublicBattles error", error);
      container.innerHTML =
        '<div class="battle-history-item">Ошибка загрузки ленты</div>';
    }
  }

  /**
   * Просматривает детали боя
   */
  async function viewBattleDetail(battleId) {
    const telegramWebApp = getTelegramWebApp();
    const headers = getAuthHeadersWithInitData();
    if (!headers) {
      telegramWebApp?.showAlert?.(
        "Авторизуйтесь через Telegram Mini App, чтобы просматривать бои.",
      );
      return;
    }
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/battle/${battleId}`, {
        headers,
      });

      if (!response.success) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(response.error || "Бой не найден");
        }
        return;
      }

      const battle = response.data.battle;
      if (!battle) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Бой не найден");
        }
        return;
      }

      const stackResets = (battle.log || []).filter(
        (e) => e.event === "stack_reset",
      ).length;
      const evolutions = (battle.log || []).filter(
        (e) => e.event === "evolution",
      ).length;
      const winner = getBattleWinner(battle);

      // Сначала загружаем данные боя в состояние
      if (loadBattleTimeline && typeof loadBattleTimeline === "function") {
        loadBattleTimeline({ battle, summary: { winner } });
      }

      // Затем открываем модальное окно
      if (showBattleModal && typeof showBattleModal === "function") {
        showBattleModal();
      }

      // Рендерим сводку боя (после загрузки данных)
      if (renderBattleSummary && typeof renderBattleSummary === "function") {
        renderBattleSummary({
          battle,
          summary: {
            stackResets,
            evolutions,
            fragmentsGranted: evolutions,
            winner,
          },
          evolutionResults: [],
        });
      }
    } catch (error) {
      console.error("viewBattleDetail error", error);
    }
  }

  return {
    loadBattleHistory,
    loadPublicBattles,
    viewBattleDetail,
    renderBattleHistory,
    renderPublicBattles,
    getBattleWinner,
  };
}

